import { SideName } from "@tosize/shapes/src/lib/models/Side/SideName";
import { useFinishInputs } from "@tosize/shapes/src/lib/swr";
import { getMaxBasedOnUnit } from "@tosize/shapes/src/lib/utils";
import type { AvailableOnSide, Finish } from "@tosize/shapes/src/types/finish";
import type { FinishItemPostData } from "@tosize/shapes/src/types/workpiece";
import { ProductGroup } from "@tosize/ui";
import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { match } from "ts-pattern";
import { useConfig } from "../context/ConfigContext";
import { usePostDataContext } from "../context/PostDataContext";
import useSelectedFinishes from "./useSelectedFinishes";
import useShapes from "./useShapes";

type FinishesResponse = Record<string, Finish>;

const useFinishes = () => {
	const intl = useIntl();
	const { data: postData, workpiece } = usePostDataContext();
	const { applyFinishOnSides } = useSelectedFinishes();
	const { shape } = useShapes();
	const { apiUrl, productGroup } = useConfig();

	const applyMandatoryFinishes = (data: FinishesResponse) => {
		for (const finish of Object.values(data).filter((finish) => finish.mandatory)) {
			applyFinishOnSides(finish, finish.availableOnSides);
		}
	};

	const { data, error } = useFinishInputs(apiUrl, postData.product, shape?.code, {
		onSuccess: applyMandatoryFinishes,
	});

	const order = useMemo(() => ["afronden", "afschuinen", "boorgat", "absband", "verstek"], []);

	const ungroupedFinishes: Finish[] | undefined = useMemo(() => {
		return data
			? Object.values(data).sort(
					(a, b) => order.indexOf(a.abstract_code) - order.indexOf(b.abstract_code),
				)
			: undefined;
	}, [data, order]);

	const finishes: Finish[] | undefined = useMemo(() => {
		if (data) {
			// Because the finish code is the key of the object we should add it
			// to the object when turning it into an array
			const groupedFinishes: Record<string, Finish> = {};

			// Sorry if you have to read this, this merges all the sides of the finish on
			// side with the same finish that is available on the corner.
			for (const item of Object.values(data)) {
				if (groupedFinishes[item.abstract_code]) {
					groupedFinishes[item.abstract_code] = {
						...groupedFinishes[item.abstract_code],
						availableOnSides: [
							...(Array.isArray(item.availableOnSides) ? item.availableOnSides : []),
							...(groupedFinishes[item.abstract_code]?.availableOnSides || []),
						],
					} as Finish;
				} else {
					groupedFinishes[item.abstract_code] = item;
				}
			}

			const groupedFinishesValues = Object.values(groupedFinishes);

			return groupedFinishesValues.sort(
				(a, b) => order.indexOf(a.abstract_code) - order.indexOf(b.abstract_code),
			);
		}

		return undefined;
	}, [data, order.indexOf]);

	const isDisabled = useMemo(() => {
		return [ProductGroup.Wood_Beams, ProductGroup.Plastic_Sheet].some(
			(group) => group === productGroup,
		);
	}, [productGroup]);

	const sides = Object.entries(workpiece.finishes.side);

	const [sideFinishes, cornerFinishes] = useMemo(
		() => [
			Object.fromEntries(sides.filter(([key]) => !key.startsWith("corner"))),
			Object.fromEntries(sides.filter(([key]) => key.startsWith("corner"))),
		],
		[sides],
	);

	const getFields = useCallback(
		(value: Record<string, FinishItemPostData[]>[]) => {
			const fields: string[] = [];

			if (finishes) {
				const finishTypes = value.map((side) => Object.keys(side)[0]);
				const occurrences = finishTypes.reduce(
					(acc, curr) => {
						if (acc[String(curr)]) {
							++acc[String(curr)]!;
						} else {
							acc[String(curr)] = 1;
						}
						return acc;
					},
					{} as Record<string, number>,
				);

				Object.entries(occurrences).map(([key, value]) => {
					const finish = Object.values(finishes).find((finish) =>
						key.startsWith(finish.abstract_code),
					);

					finish && fields.push(`${value}x ${finish.label}`);
				});
			}
			return fields;
		},
		[finishes],
	);

	const selectedValue = useMemo(() => {
		const values: string[] = [];

		if (Object.values(sideFinishes).length) {
			const fields = getFields(Object.values(sideFinishes));

			if (fields.length) {
				values.push(
					intl.formatMessage(
						{ defaultMessage: "Zijde(s): {fields}", id: "Xi31De" },
						{ fields: intl.formatList(fields, { type: "conjunction" }) },
					),
				);
			}
		}

		if (Object.values(cornerFinishes).length) {
			const fields = getFields(Object.values(cornerFinishes));

			if (fields.length) {
				values.push(
					intl.formatMessage(
						{ defaultMessage: "Hoek(en): {fields}", id: "VQN4z+" },
						{ fields: intl.formatList(fields, { type: "conjunction" }) },
					),
				);
			}
		}

		return values.length ? values.join(" | ") : undefined;
	}, [cornerFinishes, getFields, intl, sideFinishes]);

	const availableOnSides = useMemo(() => {
		return finishes
			?.flatMap((finish) => finish.availableOnSides)
			.filter((v, i, a) => a.findIndex((t) => t.sideCode === v.sideCode) === i);
	}, [finishes]);

	const finishInputs = useMemo(() => {
		return availableOnSides?.map((side) => ({
			category: side.sideCategory,
			code: side.sideCode,
			name: side.sideName,
		}));
	}, [availableOnSides]);

	function disabledByRightAngle(side: AvailableOnSide, checked: boolean) {
		const currentInput = shape?.inputs.find((input) => side.sideCode.startsWith(input.id));

		const oppositeSide = match(side.sideCode)
			.with(SideName.Width1, () => SideName.Width2)
			.with(SideName.Width2, () => SideName.Width1)
			.with(SideName.Length1, () => SideName.Length2)
			.with(SideName.Length2, () => SideName.Length1)
			.otherwise(() => undefined);

		const oppositeSideFinish = oppositeSide
			? workpiece.finishes.side[oppositeSide]?.[side.finishCode]
			: undefined;

		const rightAngleSide = currentInput?.id.includes("width") ? "length" : "width";
		const rightAngleInput = shape?.inputs.find((input) => input.id.includes(rightAngleSide));

		const margin = 2;
		const marginMultiplier = checked
			? 2
			: (oppositeSideFinish?.length ? Number(oppositeSideFinish?.length) : 0) + 1;

		const rightAngleMax = rightAngleInput
			? getMaxBasedOnUnit(rightAngleInput.max, postData.unit) - marginMultiplier * margin
			: undefined;

		return Number(workpiece.shape_dimensions[rightAngleSide]) > Number(rightAngleMax);
	}

	return {
		ungroupedFinishes,
		finishes,
		isLoading: !(data || error) && !!shape?.code,
		isDisabled,
		selectedValue,
		availableOnSides,
		finishInputs,
		disabledByRightAngle,
	};
};

export default useFinishes;
